import React, { useState, useEffect, useCallback } from 'react';
import Toggle from 'react-toggle';
import "react-toggle/style.css";
import JSONInput from 'react-json-editor-ajrm';
import locale    from 'react-json-editor-ajrm/locale/en';
import SRGLetterboxPlayer from './components/SRGLetterboxPlayer';
import SRGStreams from './components/SRGStreams';
import SRGConsole from './components/SRGConsole';
import SRGLetterboxPlayerEvents from './components/SRGLetterboxPlayerEvents';
import SRGLetterboxPlayerSettings from './components/SRGLetterboxPlayerSettings';
import SRGLetterboxPlayerControls from './components/SRGLetterboxPlayerControls';
import SRGLetterboxComponents from './components/SRGLetterboxComponents';
import { STREAMS, DEFAULT, BUCKET, BRANCHES } from './utils/config';
import UrlParams from './utils/UrlParams';
import './App.css';

const { fetch: origFetch } = window;
window.fetch = async (firstParam, ...args) => {
  if (typeof firstParam === 'string' && firstParam.includes('/sam/')) {
    const url = new URL(firstParam);
    url.searchParams.append('forceSAM', 'true');
    firstParam = url.toString();
  }
  return origFetch(firstParam, ...args);
};

function App() {
  const [branch, setBranch] = useState(UrlParams.get('branch') || DEFAULT.branch);
  const [env, setEnv] = useState(UrlParams.get('env') || UrlParams.get('server') || DEFAULT.env);
  const [lang, setLang] = useState(UrlParams.get('lang') || DEFAULT.lang);
  const [URN, setURN] = useState(UrlParams.get('urn') || DEFAULT.URN);
  const [debug, setDebug] = useState(UrlParams.has('debug'));
  const [continuousPlayback, setContinuousPlayback] = useState(UrlParams.has('continuousPlayback') || false);
  const [recommendations, setRecommendations] = useState(UrlParams.has('recommendations') || false);
  const [standalone, setStandalone] = useState(UrlParams.has('standalone'));
  const [pendingSeek, setPendingseek] = useState(UrlParams.get('pendingSeek') || 0);
  const [resizable, setResizable] = useState(UrlParams.has('resizable'));
  const [mobile, setMobile] = useState(UrlParams.has('mobile'));
  const [playerBar, setPlayerBar] = useState(UrlParams.has('playerBar'));
  const [ratio, setRatio] = useState(UrlParams.get('ratio') || DEFAULT.ratio);
  const [size, setSize] = useState(UrlParams.get('size') || DEFAULT.size);
  const [castAppId, setCastAppId] = useState(UrlParams.get('castAppId') || DEFAULT.castAppId);
  const [storage, setStorage] = useState(UrlParams.has('storage') || false);
  const [letterboxInstance, setLetterboxInstance] = useState();
  const [hardConfiguration, setHardConfiguration] = useState(null);
  const [currentConfiguration, setCurrentConfiguration] = useState({});
  const [isConfigurationOnTheFly, setIsConfigurationOnTheFly] = useState(true);
  const [isConfigurationValid, setIsConfigurationValid] = useState(true);
  const [configurationToApply, setConfigurationToApply] = useState({});
  const [subtitle, setSubtitle] = useState(UrlParams.get('subtitle') || false);

  const configurationUpdate = useCallback((event) => {
    if (!letterboxInstance.isNewApi) {
      return;
    }

    if (!isConfigurationOnTheFly) {
      setHardConfiguration(configurationToApply);
      return;
    }

    letterboxInstance.updateConfiguration(configurationToApply);
  }, [letterboxInstance, isConfigurationOnTheFly, setHardConfiguration, configurationToApply]);

  const checkConfiguration = useCallback((event) => {
    if (event.error || !letterboxInstance.isNewApi) {
      setIsConfigurationValid(false);
      return;
    }
    setIsConfigurationValid(true);
    setConfigurationToApply(event.jsObject);
  }, [letterboxInstance, setIsConfigurationValid, setConfigurationToApply]);

  useEffect(() => {
    UrlParams.set('lang', lang);
    UrlParams.set('env', env);
    UrlParams.set('branch', branch);
    UrlParams.set('ratio', ratio);
    UrlParams.set('debug', debug);
    UrlParams.set('continuousPlayback', continuousPlayback);
    UrlParams.set('recommendations', recommendations);
    UrlParams.set('resizable', resizable);
    UrlParams.set('mobile', mobile);
    UrlParams.set('size', size);
    UrlParams.set('urn', URN);
    UrlParams.set('standalone', standalone);
    UrlParams.set('pendingSeek', pendingSeek);
    UrlParams.set('playerBar', playerBar);
    UrlParams.set('castAppId', castAppId);
    UrlParams.set('storage', storage);
    UrlParams.set('subtitle', subtitle);
  }, [lang, env, branch, URN, debug, resizable, ratio, mobile, size, pendingSeek, standalone, continuousPlayback, recommendations, playerBar, castAppId, storage, subtitle]);

  useEffect(() => {
    if (branch.substring(0, 3) === 'pr/' && !BRANCHES.find(({ value }) => value === branch)) {
      BRANCHES.push({
        label: branch,
        value: branch,
      });
    }
  }, [branch]);

  useEffect(() => {
    if (mobile) {
      setRatio(2);
      setSize('small');
    }
  }, [mobile]);

  useEffect(() => {
    async function fetchData() {
      const response = await fetch(`https://${BUCKET}/production/versions.json`);
      const versions = await response.json();

      if (!versions) {
        return;
      }

      const lastVersions = [...new Set(versions
        .map(version => version.split('.').slice(0, 2).join('.')))];

      lastVersions.slice(Math.max(lastVersions.length - 3, 0)).forEach(version => {
        BRANCHES.push({
          label: `v${version}`,
          value: `v/${version}`,
        });
      });
    }
    fetchData();
  }, []);

  const setMobileMode = useCallback((newMobileValue) => {
    if (!newMobileValue) {
      setRatio(DEFAULT.ratio);
      setSize(DEFAULT.size);
    }
    setMobile(newMobileValue);
  }, []);

  const setDefaultBranch = useCallback(() => {
    setBranch(DEFAULT.branch);
  }, [setBranch]);

  const getConfigurationWithoutComponents = useCallback(() => {
    if (!isConfigurationOnTheFly) {
      return letterboxInstance.getConfiguration();
    }
    const { components, fillMode, container, debug, tagCommanderScriptURL, playerFocus, ...configuration } = letterboxInstance.getConfiguration();
    return configuration;
  }, [letterboxInstance, isConfigurationOnTheFly]);

  useEffect(() => {
    if (letterboxInstance && letterboxInstance.isNewApi && letterboxInstance.on) {
      setCurrentConfiguration(getConfigurationWithoutComponents());
      letterboxInstance.player.on('srgssr/configurationChange', () => {
        setCurrentConfiguration(getConfigurationWithoutComponents());
        setIsConfigurationValid(false);
      });
    }
  }, [getConfigurationWithoutComponents, setCurrentConfiguration, setIsConfigurationValid, letterboxInstance]);

  return (
    <div className="letterbox-web-demo">
      <header className="topbar">
        <h1 className="topbar__title">SRG Letterbox Web Demo <span className="topbar__title__branch">[{branch}]</span></h1>
        <div className="topbar__menu">
          <a href="https://github.com/SRGSSR/srgletterbox-web/" target="_blank" rel="noopener noreferrer">GIT</a>
          <a href={`https://${BUCKET}/${branch}/docs/index.html`} target="_blank" rel="noopener noreferrer">Documentation</a>
          <a href={`https://${env}/integrationlayer/2.0/mediaComposition/byUrn/${URN}.json`} target="_blank" rel="noopener noreferrer">Media Composition</a>
          <a href={`https://${env}/integrationlayer/2.0/mediaComposition/byUrn/${URN}?forceSAM=true`} target="_blank" rel="noopener noreferrer">Media Composition (SAM)</a>
        </div>
      </header>
      <div className="content">
        <div className={`content__player content__box content__player__${size}`}>
          <SRGLetterboxPlayer
            URN={URN}
            branch={branch}
            lang={lang}
            env={env}
            debug={debug}
            continuousPlayback={continuousPlayback}
            recommendations={recommendations}
            resizable={resizable}
            ratio={ratio}
            standalone={standalone}
            pendingSeek={pendingSeek}
            setLetterbox={setLetterboxInstance}
            isMobile={mobile}
            isPlayerBar={playerBar}
            castAppId={castAppId}
            storage={storage}
            hardConfiguration={hardConfiguration}
            api={{ setDefaultBranch }}
            subtitle={subtitle}
          />
        </div>
        <div className="content__sections">
          <div className="streams-section content__box">
            <SRGStreams streams={STREAMS} URN={URN} onClickAction={setURN} />
          </div>
          <div className="controls-section content__box">
            <SRGLetterboxPlayerControls letterboxInstance={letterboxInstance} />
          </div>
          <div className="settings-section content__box">
            <SRGLetterboxPlayerSettings
              lang={lang}
              env={env}
              branch={branch}
              debug={debug}
              continuousPlayback={continuousPlayback}
              recommendations={recommendations}
              resizable={resizable}
              URN={URN}
              ratio={ratio}
              mobile={mobile}
              playerBar={playerBar}
              size={size}
              standalone={standalone}
              castAppId={castAppId}
              storage={storage}
              subtitle={subtitle}
              letterbox={letterboxInstance}
              api={{ setLang, setBranch, setURN, setEnv, setDebug, setResizable, setRatio, setMobile: setMobileMode, setPlayerBar, setSize, setStandalone, setPendingseek, setContinuousPlayback, setRecommendations, setCastAppId, setStorage, setSubtitle }}
            />
          </div>
          <div className="events-section content__box">
            <SRGLetterboxPlayerEvents letterboxInstance={letterboxInstance} />
          </div>
          <div className="components-section content__box">
            <SRGLetterboxComponents letterboxInstance={letterboxInstance} />
          </div>
          <div className="console-section content__box">
            <SRGConsole />
          </div>
          {letterboxInstance?.isNewApi && <div className="configuration-section content__box">
            <h1 className="content__title">Raw Configuration</h1>
            <div className="raw-configuration-menu">
              <div className="form-control">
                <label htmlFor="configurationMode">On the fly : </label>
                <Toggle
                  defaultChecked={isConfigurationOnTheFly}
                  icons={false}
                  className='srg-toggle'
                  onChange={evt => setIsConfigurationOnTheFly(evt.target.checked)} />
              </div>
              {isConfigurationValid && <button onClick={configurationUpdate} className="controls__button">Apply</button>}
            </div>

            <JSONInput
              id='lbwConfig'
              placeholder={currentConfiguration}
              locale={locale}
              confirmGood={false}
              onChange={checkConfiguration}
              height='550px'
              width='100%'
            />
          </div>}
        </div>
      </div>
    </div>
  );
}

export default App;